<template>
  <v2-modal
    :show="showExportModal"
    title="Export cart"
    size="sm"
    @close="closePopup"
  >
    <!-- Format selector -->
    <div>
      <p
        style="font-size: 0.875rem; font-weight: 600; color: var(--v2-primary); margin-bottom: 0.75rem"
      >
        Select a file format
      </p>
      <div
        class="v2-export-modal__format-grid"
        :style="`grid-template-columns: repeat(${filteredDownloadOptions.length}, 1fr)`"
      >
        <div
          v-for="fileFormat in filteredDownloadOptions"
          :key="fileFormat.value"
          class="v2-export-modal__format-tile"
          :class="{ 'v2-export-modal__format-tile--active': downloadType && downloadType.value === fileFormat.value }"
          @click="onSelectType(fileFormat)"
        >
          <component :is="getComponent(fileFormat.fileType)" />
          <span class="v2-export-modal__format-label">{{ fileFormat.text }}</span>
        </div>
      </div>
    </div>

    <!-- PDF/non-XLSX options -->
    <div
      v-if="!isDownloadTypeXlsx && downloadType"
      class="v2-export-modal__options"
    >
      <h5>Set {{ downloadType.text }} options:</h5>
      <div class="v2-export-modal__option-list">
        <label
          v-for="opt in pdfOptions"
          :key="opt.value"
          class="v2-export-modal__option-item"
        >
          <input
            v-model="selectedOptions"
            type="checkbox"
            :value="opt.value"
          >
          {{ opt.text }}
        </label>
      </div>
    </div>

    <template #footer>
      <v2-button
        variant="outline"
        @click="closePopup"
      >
        Cancel
      </v2-button>
      <v2-button
        variant="primary"
        :disabled="!downloadType || isExporting"
        :loading="isExporting"
        @click="onClickDownload"
      >
        {{ isExporting ? 'Downloading...' : 'Download' }}
      </v2-button>
    </template>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import XlsxIcon from '@/@core/assets/svg-components/XlsxIcon.vue'
import PdfIcon from '@/@core/assets/svg-components/PdfIcon.vue'
import ZipIcon from '@/@core/assets/svg-components/ZipIcon.vue'
import PptxIcon from '@/@core/assets/svg-components/PptxIcon.vue'
import constants, { CART_EXPORT_MODAL_ID, DOWNLOAD_TYPES, FILE_FORMATS } from '@/constants'
import { EXPORT_CART } from '@/store/modules/shop.module'
import analytics from '@/@core/utils/analytics'
import { formatObject } from '@/@core/utils/utils'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'

const { DOWNLOAD_OPTIONS } = constants

export default {
  name: 'V2CartExportModal',
  components: {
    V2Modal,
    V2Button,
  },
  mixins: [UserRoleMixin],
  props: {
    retailerId: {
      type: String,
      default: null,
    },
    isMultipleCart: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['on-cart-export-success', 'on-cart-export-failed', 'open-export-multiple-carts-modal', 'on-close-export-cart-modal', 'on-change-format', 'on-click-export-modal-download'],
  data() {
    return {
      showExportModal: false,
      downloadType: DOWNLOAD_OPTIONS.CART[0],
      DOWNLOAD_OPTIONS,
      CART_EXPORT_MODAL_ID,
      pdfOptions: [
        { text: 'WSP', value: 'wsp' },
        { text: 'RRP', value: 'rrp' },
        { text: 'Gender', value: 'gender' },
        { text: 'Material', value: 'material' },
        { text: 'Size', value: 'size' },
      ],
      selectedOptions: [],
      isExporting: false,
    }
  },
  computed: {
    isDownloadTypeXlsx() {
      return this.downloadType?.value === FILE_FORMATS.XLSX
    },
    filteredDownloadOptions() {
      if (this.isRetailer) {
        return DOWNLOAD_OPTIONS.CART
      }
      return DOWNLOAD_OPTIONS.CART.filter(option => option.value === FILE_FORMATS.XLSX)
    },
  },
  methods: {
    // ─── Public API ──────────────────────────────────────────────────────
    show() {
      this.showExportModal = true
    },

    // ─── Format selection ────────────────────────────────────────────────
    onSelectType(fileFormat) {
      this.downloadType = fileFormat
      this.selectedOptions = []
      this.$emit('on-change-format', fileFormat?.value)
    },

    // ─── Reset / close ───────────────────────────────────────────────────
    resetData() {
      this.selectedOptions = []
      this.downloadType = DOWNLOAD_OPTIONS.CART[0]
      this.isExporting = false
    },
    closePopup() {
      this.resetData()
      this.showExportModal = false
      this.$emit('on-close-export-cart-modal')
    },

    // ─── Export ──────────────────────────────────────────────────────────
    handleExportCart(payload, exportType) {
      const params = this.retailerId ? { retailerId: this.retailerId } : {}
      this.$store.dispatch(EXPORT_CART, { payload, params })
        .then(res => {
          this.showExportModal = false
          apiToastSuccess(res.data.message)
          this.$emit('on-cart-export-success', exportType)
        })
        .catch(err => {
          this.$emit('on-cart-export-failed', exportType, err)
        })
        .finally(() => {
          this.isExporting = false
        })
      this.handleEvents(exportType)
    },
    onClickDownload() {
      const exportType = this.downloadType?.value
      if (!exportType) {
        apiToastWarning('Please select a file format')
        return
      }
      this.$emit('on-click-export-modal-download', exportType)
      if (this.isMultipleCart) {
        this.$emit('open-export-multiple-carts-modal', { exportType, selectedOptions: this.selectedOptions })
        this.closePopup()
        return
      }
      this.isExporting = true
      const payload = {
        downloadType: DOWNLOAD_TYPES.CART_EXPORT.VALUE,
        fileType: exportType,
      }
      if (exportType !== FILE_FORMATS.XLSX) {
        payload.respectiveId = this.$store.getters.getCart._id
      }
      if (this.selectedOptions?.length) {
        payload.optionalFields = this.selectedOptions
      }
      this.handleExportCart(payload, exportType)
    },
    handleEvents(exportType) {
      let trackSubject = {}
      let trackObject = {}
      if (this.isRetailer) {
        trackObject = formatObject({ exportFileFormat: exportType })
        trackSubject = constants.TRACKS.ACTIONS.RETAILER_EXPORTS_CART
      } else {
        trackObject = formatObject({ exportFileFormat: exportType, retailerId: this.retailerId })
        trackSubject = constants.TRACKS.ACTIONS.BRAND_EXPORTS_CART_FOR_RETAILER
      }
      analytics.track(trackSubject, trackObject)
    },
    getComponent(format) {
      const formatLower = format?.toLowerCase()
      if (formatLower?.includes(FILE_FORMATS.PDF)) return PdfIcon
      switch (formatLower) {
        case FILE_FORMATS.PDF.toLowerCase(): return PdfIcon
        case FILE_FORMATS.XLSX.toLowerCase(): return XlsxIcon
        case FILE_FORMATS.ZIP.toLowerCase(): return ZipIcon
        case FILE_FORMATS.PPTX.toLowerCase(): return PptxIcon
        default: return XlsxIcon
      }
    },
  },
}
</script>
